import type { IAccount } from "@/modules/account/common/types";
import {
  BeneficiaryLegalStatus,
  DocumentType,
  LoanRepaymentType,
  PartyType,
  ResidenceType
} from "@/modules/transaction/common/enum";
import { renderSelectedAccount } from "@/shared/helpers";

interface IGetAccountOptionsProps {
  accountsList: IAccount[];
}

interface Props {
  t: (key: string) => string;
}

export const getResidencyOptions = ({ t }: Props) => [
  {
    label: t("general.resident"),
    value: ResidenceType.Resident,
  },
  {
    label: t("general.nonResident"),
    value: ResidenceType.NonResident,
  }
];

export const getPartyTypeOptions = ({ t }: Props) => [
  {
    label: t("general.naturalPerson"),
    value: PartyType.NaturalPerson,
  },
  {
    label: t("general.legalEntity"),
    value: PartyType.LegalEntity,
  },
  {
    label: t("general.privateEntrepreneur"),
    value: PartyType.PrivateEntrepreneur,
  },
];

export const getDocumentTypeOptions = ({ t }: Props) => [
  {
    label: t("general.RAPassport"),
    value: DocumentType.RAPassport,
  },
  {
    label: t("general.foreignPassport"),
    value: DocumentType.ForeignPassport,
  },
  {
    label: t("general.deathCertificate"),
    value: DocumentType.DeathCertificate,
  }
];

export const getAccountOptions = ({ accountsList }: IGetAccountOptionsProps) => {
  return accountsList
    .map((item, index) => ({
      value: item.number,
      label: renderSelectedAccount(item),
      meta: { ...item, index },
    }));
};

export const regionCodeOptions = [
  { value: 10, label: "10 • Նոր Նորք" },
  { value: 11, label: "11 • Արաբկիր" },
  { value: 12, label: "12 • Էրեբունի" },
  { value: 13, label: "13 • Նոր Նորք" },
  { value: 14, label: "14 • Մաշտոց" },
  { value: 15, label: "15 • Կենտրոն" },
  { value: 16, label: "16 • Մաշտոց" },
  { value: 17, label: "17 • Շենգավիթ" },
  { value: 18, label: "18 • Կենտրոն" },
  { value: 19, label: "19 • ՀՀ ՊԵԿ (մաքսային ծառայություն)" },
  { value: 20, label: "20 • ՊԵԿ հարկեր և տուրքեր" },
  { value: 21, label: "21 • Գյումրի" },
  { value: 22, label: "22 • Գյումրի" },
  { value: 23, label: "23 • ԽՀՎ ՀՏ" },
  { value: 24, label: "24 • Արթիկ" },
  { value: 25, label: "25 • Միջազգ. ճան. փոխադր. «ՏԻՐ»" },
  { value: 26, label: "26 • Արթիկ" },
  {
    value: 27,
    label:
      "27 • Արտաքին տնտեսական գործունեության մաքսային սպասարկման և հսկողության վարչություն (Արարատյան Մ/Տ, Հյուսիսային Մ/Տ, Արևելյան Մ/Տ, Հարավային Մ/Տ)",
  },
  { value: 28, label: "28 • Շիրակի մաքսատում" },
  { value: 29, label: "29 • Գուգարքի մաքսատուն" },
  { value: 30, label: "30 • Սյունիկի մաքսատուն" },
  { value: 31, label: "31 • Վանաձոր" },
  { value: 32, label: "32 • Վանաձոր" },
  { value: 33, label: "33 • Տարած.կոդ" },
  { value: 34, label: "34 • Վանաձոր" },
  { value: 35, label: "35 • Վանաձոր" },
  { value: 36, label: "36 • Վանաձոր" },
  { value: 37, label: "37 • Նոր Նորք" },
  { value: 38, label: "38 • Արաբկիր" },
  { value: 39, label: "39 • Մաշտոց" },
  { value: 40, label: "40 • Կենտրոն" },
  { value: 41, label: "41 • Իջևան" },
  { value: 42, label: "42 • Իջևան" },
  { value: 43, label: "43 • Իջևան" },
  { value: 44, label: "44 • Իջևան" },
  { value: 45, label: "45 • Կենտրոն" },
  { value: 46, label: "46 • Տարած.կոդ" },
  { value: 51, label: "51 • Աշտարակ" },
  { value: 52, label: "52 • Աշտարակ" },
  { value: 53, label: "53 • Աշտարակ" },
  { value: 55, label: "55 • Միջազգ. փոստ և սուրհանդակ" },
  { value: 56, label: "56 • Եղեգնաձոր" },
  { value: 57, label: "57 • Եղեգնաձոր" },
  { value: 58, label: "58 • Եղեգնաձոր" },
  { value: 60, label: "60 • Ակցիզ. հարկով հարկվող ապրանք. մասնակի մաքս." },
  { value: 61, label: "61 • Հրազդան" },
  { value: 62, label: "62 • Կոտայք" },
  { value: 63, label: "63 • Նաիրի" },
  { value: 64, label: "64 • Չարենցավան" },
  { value: 66, label: "66 • Տարած.կոդ" },
  {
    value: 70,
    label:
      "70 • Պետական սահմանի ցամաքային անցման կետերում մաքսային սպասարկման և հսկողության վարչություն (Բագրատաշեն Մ/Կ, Բավրա Մ/Կ, Գոգավան-Պրիվոլնոե Մ/Կ, Մեղրի Մ/Կ,",
  },
  { value: 71, label: "71 • Գավառ" },
  { value: 72, label: "72 • Սեվան" },
  { value: 73, label: "73 • Մարտունի" },
  { value: 74, label: "74 • Սեվան" },
  { value: 75, label: "75 • Մարտունի" },
  { value: 77, label: "77 • Ավտոտրանս. միջոցների մաքս. ձևակերպ." },
  { value: 81, label: "81 • Արմավիր" },
  { value: 82, label: "82 • Էջմիածին" },
  { value: 83, label: "83 • Արմավիր" },
  { value: 86, label: "86 • Կապան" },
  { value: 87, label: "87 • Կապան" },
  { value: 88, label: "88 • Սիսիան" },
  { value: 89, label: "89 • Գորիս" },
  {
    value: 90,
    label:
      "90 • Էլեկտրոնային առևտրի և օդային տրանսպորտով իրականացվող փոխադրումների վարչություն (Արևմտյան, Շիրակ օդանավակայան)",
  },
  { value: 91, label: "91 • Արտաշատ" },
  { value: 92, label: "92 • Արարատ" },
  { value: 94, label: "94 • Մասիս" },
  { value: 95, label: "95 • Ամփոփ հաշվանցումներ" },
  { value: 99, label: "99 • Ֆինանսների նախարարության մուտքեր" },
];

export const getLoanRepaymentTypeOptions = ({ t }: Props) => [
  {
    value: LoanRepaymentType.RegularRepayment,
    label: t(`transfer.loanRepayment.${LoanRepaymentType.RegularRepayment}`),
  },
  {
    value: LoanRepaymentType.PrincipalRepayment,
    label: t(`transfer.loanRepayment.${LoanRepaymentType.PrincipalRepayment}`),
  },
  {
    value: LoanRepaymentType.FullRepayment,
    label: t(`transfer.loanRepayment.${LoanRepaymentType.FullRepayment}`),
  },
];

export const getBeneficiaryLegalStatusOptions = ({ t }: Props) => [
  {
    label: t("general.legal"),
    value: BeneficiaryLegalStatus.Legal,
  },
  {
    label: t("general.natural"),
    value: BeneficiaryLegalStatus.Natural,
  },
];
